import { useAudioPlayer } from '../../context/AudioPlayerProvider';
import './AudioPlayerBar.css';

function formatTime(seconds) {
  if (!Number.isFinite(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${mins}:${secs}`;
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
      <path d="M5.5 3.2v9.6L13 8z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor">
      <rect x="4" y="3" width="3" height="10" />
      <rect x="9" y="3" width="3" height="10" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

// The fixed mini-player. Sits above the bottom nav as an extension of it,
// only when a story is loaded. Shows the headline, a play/pause toggle, a
// scrubable progress rail and a close button. Equalizer bars dance while
// playing — the same micro-animation language as the podcast card.
export function AudioPlayerBar() {
  const { current, isPlaying, duration, elapsed, pause, resume, stop, seek } =
    useAudioPlayer();

  if (!current) return null;

  const progress = duration ? Math.min(100, (elapsed / duration) * 100) : 0;

  const onScrub = (event) => {
    if (!duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    seek(ratio * duration);
  };

  return (
    <div className="audio-bar" role="region" aria-label="Audio player">
      <button
        type="button"
        className="audio-bar__play"
        onClick={isPlaying ? pause : resume}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </button>

      <div className="audio-bar__body">
        <div className="audio-bar__top">
          <p className="audio-bar__title">{current.headline}</p>
          <span
            className={
              isPlaying
                ? 'audio-bar__eq audio-bar__eq--playing'
                : 'audio-bar__eq'
            }
            aria-hidden="true"
          >
            <span />
            <span />
            <span />
            <span />
            <span />
          </span>
        </div>

        <div className="audio-bar__bottom">
          <span className="audio-bar__time">{formatTime(elapsed)}</span>
          <div
            className="audio-bar__progress"
            onClick={onScrub}
            role="slider"
            tabIndex={0}
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration || 0)}
            aria-valuenow={Math.round(elapsed)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') seek(Math.min(duration, elapsed + 5));
              if (event.key === 'ArrowLeft') seek(Math.max(0, elapsed - 5));
            }}
          >
            <span
              className="audio-bar__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="audio-bar__time">{formatTime(duration)}</span>
        </div>
      </div>

      <button
        type="button"
        className="audio-bar__close"
        onClick={stop}
        aria-label="Close player"
      >
        <CloseIcon />
      </button>
    </div>
  );
}